import React, { FC, useMemo } from "react";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { useTranslation } from "react-i18next";

import CustomButton from "ui/Button";
import CustomDropDownList from "ui/dropDown";
import { ICON_NAMES } from "utils/constant";
import { getPackagesApi, getProductsApi } from "api/common";
import { RegistrationLineForm } from "interface/registration";

interface LineForm extends RegistrationLineForm {
	packaging?: string;
	lineNumber?: number;
}

interface StepTwoMobileListProps {
	form: LineForm[];
	onUpdateLine: (line: LineForm) => void;
	onSelectPackage: (line: LineForm) => void;
	onDelete: (line: LineForm) => void;
	hasMeetRequirement: (data: LineForm, field: string) => boolean;
	showRequirement: boolean;
}

const numericFields: Array<{
	field:
		| "numberOfUnits"
		| "numberOfPallets"
		| "numberOfPieces"
		| "registrationLiveWeightKg"
		| "price";
	labelKey: string;
	decimals: number;
	required: boolean;
}> = [
	{ field: "numberOfUnits", labelKey: "Units", decimals: 0, required: false },
	{ field: "numberOfPallets", labelKey: "Pallets", decimals: 0, required: false },
	{ field: "numberOfPieces", labelKey: "Pieces", decimals: 0, required: true },
	{ field: "registrationLiveWeightKg", labelKey: "Weight", decimals: 2, required: true },
	{ field: "price", labelKey: "Price", decimals: 2, required: true },
];

const isNullLike = (value: unknown) =>
	value == null || value === "null" || value === "undefined" || value === "";

const toNumberOrNull = (value: unknown): number | null => {
	if (isNullLike(value)) {
		return null;
	}

	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

const selectInputText = (event: any) => {
	const input = event?.target instanceof HTMLInputElement
		? event.target
		: event?.target?.querySelector?.("input");

	if (input && typeof input.select === "function") {
		input.select();
	}
};

const StepTwoMobileList: FC<StepTwoMobileListProps> = ({
	form,
	onUpdateLine,
	onSelectPackage,
	onDelete,
	hasMeetRequirement,
	showRequirement,
}) => {
	const { t } = useTranslation();

	const totals = useMemo(() => {
		const sum = (field: keyof LineForm) =>
			form.reduce((acc, item) => acc + (toNumberOrNull(item[field]) ?? 0), 0);

		return {
			units: sum("numberOfUnits"),
			pallets: sum("numberOfPallets"),
			pieces: sum("numberOfPieces"),
			weight: sum("registrationLiveWeightKg"),
		};
	}, [form]);

	const updateField = (
		line: LineForm,
		field: keyof LineForm,
		value: number | string | null | undefined,
	) => {
		onUpdateLine({
			...line,
			[field]: value,
		});
	};

	return (
		<div className="registration-mobile-lines">
			{form.length === 0 && (
				<div className="registration-mobile-empty">{t("LinesRequired")}</div>
			)}

			{form.map((line, index) => {
				const lineKey = `${line.id ?? "noid"}-${line.lineNumber ?? "noline"}-${index}`;
				return (
					<div key={lineKey} className="registration-line-card">
						<div className="registration-line-field">
							<label>{t("Product")}</label>
							<CustomDropDownList
								data={[]}
								value={
									!isNullLike(line.productId)
										? {
											id: line.productId,
											name: isNullLike(line.product) ? "" : line.product,
										}
										: ({} as any)
								}
								onSelect={(data) => {
									onUpdateLine({
										...line,
										productId: data.id,
										product: data.name,
										piecesMandatory:
											data.isNumberOfUnitsMandatory === true ? 1 : 0,
									});
								}}
								serverFilterApi={getProductsApi}
								defaultItem={{ id: null, name: t("ChooseProduct") }}
								filterable={true}
								serverFilter={true}
								textField="name"
								dataItemKey="id"
								showError={
									!hasMeetRequirement(line, "productId") && showRequirement
								}
							/>
						</div>

						<div className="registration-line-field">
							<label>{t("Packaging")}</label>
							<CustomDropDownList
								data={[]}
								value={
									!isNullLike(line.packagingCode)
										? {
											code: line.packagingCode,
											name: isNullLike(line.packaging) ? "" : line.packaging,
										}
										: ({} as any)
								}
								onSelect={(data) => {
									onSelectPackage({
										...line,
										packagingCode: data.code,
										packaging: data.name,
									});
								}}
								serverFilterApi={getPackagesApi}
								defaultItem={{ code: null, name: t("ChoosePackaging") }}
								filterable={true}
								serverFilter={true}
								textField="name"
								dataItemKey="code"
								showError={
									!hasMeetRequirement(line, "packagingCode") && showRequirement
								}
							/>
						</div>

						<div className="registration-line-grid">
							{numericFields.map((fieldItem) => (
								<div className="registration-line-field" key={fieldItem.field}>
									<label>{t(fieldItem.labelKey)}</label>
									<NumericTextBox
										value={toNumberOrNull(line[fieldItem.field])}
										format={fieldItem.decimals === 0 ? "n0" : "n2"}
										spinners={false}
										onFocus={selectInputText}
										onChange={(e) => {
											const value = e.value == null ? null : Number(e.value);
											updateField(line, fieldItem.field, value);
										}}
										style={{
											width: "100%",
											borderColor:
												fieldItem.required &&
												!hasMeetRequirement(line, fieldItem.field) &&
												showRequirement
													? "red"
													: undefined,
										}}
									/>
								</div>
							))}
						</div>

						<div className="registration-line-card-actions">
							<CustomButton
								onClick={() => onDelete(line)}
								label={t("DeleteLine")}
								iconName={ICON_NAMES.Delete}
								btnStyle={{ width: "100%", minHeight: "44px" }}
							/>
						</div>
					</div>
				);
			})}

			{form.length > 0 && (
				<div className="registration-mobile-totals">
					<div>
						{t("Units")}: {totals.units.toFixed(0)}
					</div>
					<div>
						{t("Pallets")}: {totals.pallets.toFixed(0)}
					</div>
					<div>
						{t("Pieces")}: {totals.pieces.toFixed(0)}
					</div>
					<div>
						{t("Weight")}: {totals.weight.toFixed(2)} kg
					</div>
				</div>
			)}
		</div>
	);
};

export default StepTwoMobileList;
